import type { MenuContribution, MenuStructure } from 'shared/core';
import { MASTER_DEFS, MASTER_ORDER } from './master-definitions';
import { MASTER_PERMISSION_MODULE } from './tannery-master.models';

/** The unit type this library serves — the value on the tenant record. */
export const TANNERY_UNIT_TYPE = 'tannery';

/**
 * The path segment the application mounts this library at, under the shell:
 * `/home/tannery/...`. A vertical library is namespaced so one canMatch guards
 * all of it and its segments can never collide with rexine's flat ones.
 */
export const TANNERY_MOUNT = 'tannery';

/** The tannery masters home — the menu's "All masters" entry and the master list's back link (no leading slash: it is a router path). Not the tenant landing: every tenant lands on the application's `homeRoute` (`/home/welcome`). */
export const TANNERY_HOME = `home/${TANNERY_MOUNT}/masters`;

/** The article master's list; the form is `${TANNERY_ARTICLES}/new` and `${TANNERY_ARTICLES}/:id`. */
export const TANNERY_ARTICLES = `home/${TANNERY_MOUNT}/articles`;

/** The recipe list; the form is `${TANNERY_RECIPES}/new` and `${TANNERY_RECIPES}/:id`. */
export const TANNERY_RECIPES = `home/${TANNERY_MOUNT}/recipes`;

/** The spec sheet list; the form is `${TANNERY_SPEC_SHEETS}/new` and `${TANNERY_SPEC_SHEETS}/:id`. */
export const TANNERY_SPEC_SHEETS = `home/${TANNERY_MOUNT}/spec-sheets`;

/** The spec sheet print route, a top-level route outside the shell layout — not a menu entry. */
export const TANNERY_SPEC_SHEET_PRINT = `${TANNERY_MOUNT}/print/spec-sheet`;

/** The sale order list; the form is `${TANNERY_SALE_ORDERS}/new` and `${TANNERY_SALE_ORDERS}/:id`. */
export const TANNERY_SALE_ORDERS = `home/${TANNERY_MOUNT}/sale-orders`;

/** The production order list; the form is `${TANNERY_PRODUCTION_ORDERS}/new` and `${TANNERY_PRODUCTION_ORDERS}/:id`. */
export const TANNERY_PRODUCTION_ORDERS = `home/${TANNERY_MOUNT}/production-orders`;

/** The batch list; the create screen is `${TANNERY_BATCHES}/new` (optionally `?productionOrderId=`) and the detail page is `${TANNERY_BATCHES}/:id`. */
export const TANNERY_BATCHES = `home/${TANNERY_MOUNT}/batches`;

/** The Production Flow board — one batch's stages, grouped by phase (Sprint 15a Phase 6). Optionally `?batchId=` to deep-link a selection. */
export const TANNERY_PRODUCTION_FLOW = `home/${TANNERY_MOUNT}/production-flow`;

/**
 * Sprint 8 design D17: one stage's own detail page, mounted under its batch
 * — `/home/tannery/batches/:id/stages/:stageLogId` — the second recorded
 * deviation from the drawer pattern, like the batch page itself. Not a menu
 * entry (there is no leaf for it); reached only from the batch page's
 * Details button and the batch list is unaffected.
 */
export function TANNERY_BATCH_STAGE(batchId: number, stageLogId: number): string {
  return `/${TANNERY_BATCHES}/${batchId}/stages/${stageLogId}`;
}

/** The grading gate list; the gate itself is `TANNERY_GRADING_GATE(stageLogId)` — a page (Sprint 9 design D14). */
export const TANNERY_GRADING = `home/${TANNERY_MOUNT}/grading`;

/** The leather stock list; a receipt is `TANNERY_OUTPUT_RECEIPT(outputId)` — a page (Sprint 9 design D14). */
export const TANNERY_LEATHER_STOCK = `home/${TANNERY_MOUNT}/leather-stock`;

/** The chemical forecast list; the builder is `${TANNERY_FORECASTS}/new` and `${TANNERY_FORECASTS}/:id`. */
export const TANNERY_FORECASTS = `home/${TANNERY_MOUNT}/forecasts`;

/** The material issue screen; reached bare from the menu, or pre-scoped from a stage or a production order. */
export const TANNERY_MATERIAL_ISSUE = `home/${TANNERY_MOUNT}/material-issue`;

/** The drum schedule gantt. */
export const TANNERY_DRUM_SCHEDULE = `home/${TANNERY_MOUNT}/drum-schedule`;

/** The batch costing list; a sheet's own review/override screen is `${TANNERY_BATCH_COSTING}/:id`. */
export const TANNERY_BATCH_COSTING = `home/${TANNERY_MOUNT}/batch-costing`;

/** The live monitor — occupancy and alerts on one screen. */
export const TANNERY_LIVE_MONITOR = `home/${TANNERY_MOUNT}/live-monitor`;

/** Sprint 15a: the tannery dashboard — KPI cards, status breakdown, needs-attention and recent batches, all assembled client-side from calls the individual screens already make. */
export const TANNERY_DASHBOARD = `home/${TANNERY_MOUNT}/dashboard`;

/** Sprint 19: the sample request list; the form is `${TANNERY_SAMPLES}/new` and the detail is opened as a drawer from the list, not a page. */
export const TANNERY_SAMPLES = `home/${TANNERY_MOUNT}/samples`;

/** Sprint 20: the outward movement list; the form is `${TANNERY_OUTWARD_MOVEMENTS}/new` and `${TANNERY_OUTWARD_MOVEMENTS}/:id`. There is no gate-out screen or `Tannery - Gate Out` permission (plan §11, decided 2026-09-18) — gate-out is worked entirely from the platform's own common gate module once a movement registers itself with `dbo.GateInTable` at Approve. */
export const TANNERY_OUTWARD_MOVEMENTS = `home/${TANNERY_MOUNT}/outward-movements`;

/** Sprint 21: the inward return list; the form is `${TANNERY_INWARD_RETURNS}/new` and `${TANNERY_INWARD_RETURNS}/:id`. Picks a gated-out outward movement and returns some of its packages — Approve is the stock restore. */
export const TANNERY_INWARD_RETURNS = `home/${TANNERY_MOUNT}/inward-returns`;

/** Sprint 22: the job work order list; the form is `${TANNERY_JOB_WORKS}/new` and `${TANNERY_JOB_WORKS}/:id`. A batch's own contiguous stage range sent to another tannery, with a process sheet and chemicals from our store — Receive and Close (the batch coming home) are Sprint 23. */
export const TANNERY_JOB_WORKS = `home/${TANNERY_MOUNT}/job-work`;

/** The job work process sheet / challan print route, a top-level route outside the shell layout — not a menu entry. */
export const TANNERY_JOB_WORK_PRINT = `${TANNERY_MOUNT}/print/job-work`;

/** The outward movement (delivery challan) print route, a top-level route outside the shell layout — not a menu entry. */
export const TANNERY_OUTWARD_MOVEMENT_PRINT = `${TANNERY_MOUNT}/print/outward-movement`;

/** The lot list (registration is a modal on this same screen); a lot's own detail page is `${TANNERY_TRACEABILITY}/:id`, and the two traceability reports are `${TANNERY_TRACEABILITY}/reports`. */
export const TANNERY_TRACEABILITY = `home/${TANNERY_MOUNT}/traceability`;

/**
 * Sprint 24: the tannery period reports. The bare path redirects to the first report — a
 * "hub" page listing the reports would only repeat what the sidebar already shows.
 *
 * Distinct from `${TANNERY_TRACEABILITY}/reports`, which is Sprint 15's two lot-traceability
 * lookups and stays where it is: those are keyed by one id and read from the transactional
 * app, not period reports over a window.
 */
export const TANNERY_REPORTS = `home/${TANNERY_MOUNT}/reports`;

/** R1 — what was produced in a period, by article, grade and kind of output. */
export const TANNERY_REPORT_PRODUCTION_SUMMARY = `${TANNERY_REPORTS}/production-summary`;

/** R2 — what went into the drums against what the recipes called for. */
export const TANNERY_REPORT_CHEMICAL_CONSUMPTION = `${TANNERY_REPORTS}/chemical-consumption`;

/** R3 — square feet of finished leather per kilogram of hide, against the spec sheet. */
export const TANNERY_REPORT_YIELD_SUMMARY = `${TANNERY_REPORTS}/yield-summary`;

/**
 * Sprint 25. The colour standards a batch's leather is matched against. A master, beside
 * Articles — the Integration Spec and the schema mapping both classify it as one.
 */
export const TANNERY_SHADE_CARDS = `home/${TANNERY_MOUNT}/shade-cards`;

/** Sprint 25. Physical testing on a batch, judged against a specification sheet. */
export const TANNERY_LAB_TESTS = `home/${TANNERY_MOUNT}/lab-tests`;

/** Sprint 4b. What customers owe, derived from the payment ledger. */
export const TANNERY_RECEIVABLES = `home/${TANNERY_MOUNT}/receivables`;

/** R4 — what an article cost per kg, piece or sqft, and which head moved. */
export const TANNERY_REPORT_BATCH_COST = `${TANNERY_REPORTS}/batch-cost`;

/** R5 — which stage runs over its planned hours, and how busy each drum is. */
export const TANNERY_REPORT_STAGE_CYCLE_TIME = `${TANNERY_REPORTS}/stage-cycle-time`;

/** R6 — per sale order line, what gated out, what the customer sent back and what they therefore have. */
export const TANNERY_REPORT_SALE_ORDER_FULFILMENT = `${TANNERY_REPORTS}/sale-order-fulfilment`;

/**
 * D1 — one batch's whole history as a printable document, outside the shell beside the other
 * print routes. Not under `home/tannery`: a print page is opened in its own tab and closes
 * itself, so it must not carry the sidebar.
 */
export const TANNERY_BATCH_HISTORY_PRINT = `tannery/print/batch-history`;

/** Pre-scoped to one batch stage, the way the stage page links to it. */
export function TANNERY_MATERIAL_ISSUE_FOR_STAGE(batchStageLogId: number, label: string): { path: string; queryParams: Record<string, string> } {
  return { path: `/${TANNERY_MATERIAL_ISSUE}`, queryParams: { batchStageLogId: String(batchStageLogId), label } };
}

/** Sprint 9 design D14: one gate's own page, `/home/tannery/grading/:stageLogId` — the third recorded deviation from the drawer pattern (a sorting table is a tablet-first place of work, and the prototype's grading gate is a page). */
export function TANNERY_GRADING_GATE(stageLogId: number): string {
  return `/${TANNERY_GRADING}/${stageLogId}`;
}

/** Sprint 9 design D14: one output's own receipt page, `/home/tannery/leather-stock/receive/:outputId` — the fourth recorded deviation from the drawer pattern (a packing bench is a tablet-first place of work). */
export function TANNERY_OUTPUT_RECEIPT(outputId: number): string {
  return `/${TANNERY_LEATHER_STOCK}/receive/${outputId}`;
}

/**
 * What the tannery vertical adds to the shared Master family — the one
 * family it shares with rexine, since a tenant's masters and customers
 * belong together regardless of vertical. The leather masters sit under a
 * `Tannery Masters` sub-family so they read as a set and stay distinct from
 * the rexine masters beside them; a pathless node with children is exactly
 * how the rexine families in that group are shaped.
 *
 * Sales, Planning and Production are NOT here — they are tannery's own
 * top-level groups (`TANNERY_MENU_GROUPS` below). They used to be grafted
 * into rexine's shared Production and Sales families, which put a tannery
 * tenant's screens beside rexine's Formulation Code / Pigment MB / Mixing /
 * Vessel Tracker — none of it theirs, and no heading of its own to tell the
 * two apart. ADR-P-002 v2.0 (amending D6) keeps the shared-family pattern
 * only for families genuine to both verticals — which, as of Sprint 15a, also
 * covers `inventory` (Leather stock) and `costing` (Batch costing) below:
 * single items with nothing tannery-specific about the concept itself, unlike
 * Sales/Production's wholly different domain content.
 *
 * Every leaf carries its module's View responsibility, so the shell shows it
 * only when the tenant holds that operation (seeded by unit-type scope from the
 * admin catalogue) and the user's role grants it. The API answers the same
 * question server-side; nothing here is a security boundary, only menu hygiene.
 */
export const TANNERY_MENU_CONTRIBUTIONS: MenuContribution[] = [
  {
    groupId: 'master',
    children: [
      {
        path: '',
        label: 'Tannery Masters',
        icon: 'database',
        // EVERY KIND CARRIES ITS OWN MODULE; THE SUB-FAMILY CARRIES NONE, AND THE HUB IS AN
        // ANY-OF OVER THE KINDS. The permission-naming migration split the single bundled
        // `Tannery - Masters` into one `Master - <Kind>` module per kind, so there is no bundled
        // module left to guard either with.
        //
        // Not the bare `{ module: 'Master' }` family guard: `AuthService.CheckModule` substring-
        // matches it, so every rexine user holding the rexine `Master` module passed it, and the
        // hub alone kept an otherwise empty Tannery Masters submenu in their sidebar. A pathless
        // node with no permission renders only when one of its children does (`buildMenu`), so
        // the submenu now appears exactly when the user can view at least one tannery master — a
        // rexine tenant never can, because the admin catalogue seeds those modules by unit type.
        // The API enforces the real rule regardless of what the menu shows.
        children: [
          {
            path: `/${TANNERY_HOME}`,
            label: 'All masters',
            icon: 'appstore',
            // The hub lists every kind with its row count and is useful to anyone holding any
            // of them: a kind the caller may not read simply shows a dash. So it takes an any-of
            // over every kind rather than one kind's module, which would arbitrarily privilege
            // whichever kind was named.
            permission: {
              anyOf: MASTER_ORDER.map((kind) => ({ module: MASTER_PERMISSION_MODULE[kind], responsibility: 'View' })),
            },
          },
          // One module per kind, straight off the map the migration added for exactly this.
          // Equipment is no longer the one exception it used to be — before the split it was
          // the only kind with a module of its own, and every other kind rode the bundled
          // `Tannery - Masters`.
          ...MASTER_ORDER.map((kind) => ({
            path: `/${TANNERY_HOME}/${kind}`,
            label: MASTER_DEFS[kind].title,
            permission: { module: MASTER_PERMISSION_MODULE[kind], responsibility: 'View' },
          })),
        ],
      },
      {
        path: `/${TANNERY_SHADE_CARDS}`,
        label: 'Shade Cards',
        icon: 'bg-colors',
        permission: { module: 'Master - Shade Card', responsibility: 'View' },
        children: [
          { path: `/${TANNERY_SHADE_CARDS}/new`, label: 'New shade card', hidden: true },
          { path: `/${TANNERY_SHADE_CARDS}/:id`, label: 'Shade card', hidden: true },
        ],
      },
      {
        path: `/${TANNERY_ARTICLES}`,
        label: 'Articles',
        icon: 'tags',
        permission: { module: 'Master - Article', responsibility: 'View' },
        children: [
          { path: `/${TANNERY_ARTICLES}/new`, label: 'New article', hidden: true },
          { path: `/${TANNERY_ARTICLES}/:id`, label: 'Article', hidden: true },
        ],
      },
    ],
  },
  // Leather stock and Batch costing are each a single item with nothing to
  // group them with — no 'Finished Leather' sub-family, the same way Articles
  // is a direct item of Master rather than wrapped in one. Neither `inventory`
  // nor `costing` carries a `unitType` (unlike `sales`/`production`): both are
  // genuinely shared, generic concepts a rexine tenant already uses, just with
  // nothing tannery-side to put in them until now.
  {
    groupId: 'inventory',
    children: [
      {
        path: `/${TANNERY_LEATHER_STOCK}`,
        label: 'Leather stock',
        icon: 'inbox',
        permission: { module: 'Production - Output Receipt', responsibility: 'View' },
        children: [{ path: `/${TANNERY_LEATHER_STOCK}/receive/:outputId`, label: 'Receive', hidden: true }],
      },
    ],
  },
  {
    groupId: 'costing',
    children: [
      {
        path: `/${TANNERY_BATCH_COSTING}`,
        label: 'Batch costing',
        icon: 'dollar',
        permission: { module: 'Costing - Batch', responsibility: 'View' },
        children: [{ path: `/${TANNERY_BATCH_COSTING}/:id`, label: 'Batch costing sheet', hidden: true }],
      },
    ],
  },
  // Sprint 24. The tannery period reports go into the SHARED `reports` group, under a
  // `Tannery Reports` sub-family — the same shape the `Tannery Masters` sub-family above
  // uses, and for the same reason: they read as a set and stay distinct from the rexine
  // reports beside them.
  //
  // Not a new top-level group: `reports` carries no `unitType`, so it already renders for a
  // tannery tenant, and until now it was empty in practice for one. This closes ADR-P-002
  // §8's open item.
  //
  // EVERY LEAF CARRIES ITS OWN PERMISSION, AND THE SUB-FAMILY CARRIES NONE. The
  // permission-naming migration split the single bundled 'Tannery - Reports' into one module
  // per report, so a grant can now be as narrow as one report — money (Batch Cost) is a
  // different audience from production. There is no bundled module left to guard the
  // sub-family with, and naming one report's module here would hide the whole submenu from
  // somebody granted only another one.
  //
  // A pathless node with no permission is exactly the any-of guard this needs: `buildKfMenu`
  // renders a group when at least one of its children is permitted, and drops it entirely
  // when none is ("an empty submenu is worse than no submenu"). So Tannery Reports appears
  // for anyone holding any one report and disappears for anyone holding none, with no extra
  // machinery. Menu hygiene only — every endpoint re-checks its own module server side.
  {
    groupId: 'reports',
    children: [
      {
        path: '',
        label: 'Tannery Reports',
        icon: 'bar-chart',
        openMapKey: 'tannery-reports',
        children: [
          {
            path: `/${TANNERY_REPORT_PRODUCTION_SUMMARY}`,
            label: 'Production Summary',
            icon: 'line-chart',
            permission: { module: 'Reports - Production Summary', responsibility: 'View' },
          },
          {
            path: `/${TANNERY_REPORT_CHEMICAL_CONSUMPTION}`,
            label: 'Chemical Consumption',
            icon: 'experiment',
            permission: { module: 'Reports - Chemical Consumption', responsibility: 'View' },
          },
          {
            path: `/${TANNERY_REPORT_YIELD_SUMMARY}`,
            label: 'Yield Summary',
            icon: 'fund',
            permission: { module: 'Reports - Yield Summary', responsibility: 'View' },
          },
          {
            path: `/${TANNERY_REPORT_BATCH_COST}`,
            label: 'Batch Cost',
            icon: 'dollar',
            // The menu names ONE module, as every entry here does. The endpoint requires this
            // AND 'Costing - Batch' View, so a role holding only this one sees the leaf and is
            // refused on opening it. That is the shell's single-permission guard, not a gap in
            // the check — and the screen says which second responsibility to ask for.
            permission: { module: 'Reports - Batch Cost', responsibility: 'View' },
          },
          {
            path: `/${TANNERY_REPORT_STAGE_CYCLE_TIME}`,
            label: 'Stage Cycle Time',
            icon: 'clock-circle',
            permission: { module: 'Reports - Stage Cycle Time', responsibility: 'View' },
          },
          {
            path: `/${TANNERY_REPORT_SALE_ORDER_FULFILMENT}`,
            label: 'Sale Order Fulfilment',
            icon: 'car',
            permission: { module: 'Reports - Sale Order Fulfilment', responsibility: 'View' },
          },
        ],
      },
    ],
  },
];

/**
 * Tannery's own top-level menu families — visible only to a tannery tenant
 * (`unitType: TANNERY_UNIT_TYPE`; `groupsForUnitType` in `shared` drops them
 * for everyone else, deterministically, whatever the operations catalogue
 * holds). Spliced into the shared list with `withMenuGroupsAfter` rather than
 * grafted onto a rexine family (ADR-P-002 v2.0, amending D6).
 *
 * The families carry no `permission` of their own: a family renders when at
 * least one screen inside it is permitted (`buildMenu` drops a family with no
 * visible children, and every screen here names its own module). They used to
 * carry 'Tannery - Sales' / 'Planning' / 'Production' / 'Dispatch', meant as a
 * one-grant shortcut for a whole family, but that never worked: each screen
 * still checks its own module, so a role holding only the family module saw an
 * empty family and nothing rendered. Visibility follows the screens alone.
 *
 * Grouped by who opens the screen and where in the order-to-leather flow it
 * sits, the way rexine's own families are, not by production phase — no
 * screen is scoped to one of the six phases (Beamhouse … Dispatch); a batch
 * runs through all of them, and the phase shows up inside the batch/stage
 * pages and the drum schedule instead.
 */
/**
 * Sprint 15a Phase 0: the tannery dashboard's own top-level group — a
 * separate array from `TANNERY_MENU_GROUPS` below because it anchors on a
 * different id (`'dashboard'`, the now rexine-scoped shared group) so it
 * lands right after Home/rexine's own dashboard, matching the prototype
 * putting its dashboard first — not after Reports, where the other four
 * groups splice in. One leaf, one permission (`Dashboard - Tannery Dashboard`, View
 * only, seeded by `Tannery-Phase21-Dashboard-DEPLOY.sql` — a read-only
 * aggregate screen has nothing to Add/Edit).
 */
export const TANNERY_DASHBOARD_MENU_GROUPS: MenuStructure[] = [
  {
    id: 'tannery-dashboard',
    label: 'Dashboard',
    icon: 'dashboard',
    permission: { module: 'Dashboard - Tannery Dashboard' },
    openMapKey: 'tannery-dashboard',
    dataLabel: 'Dashboard',
    collapsedHeading: 'Dashboard',
    unitType: TANNERY_UNIT_TYPE,
    children: [
      {
        path: `/${TANNERY_DASHBOARD}`,
        label: 'Dashboard',
        icon: 'dashboard',
        permission: { module: 'Dashboard - Tannery Dashboard', responsibility: 'View' },
      },
    ],
  },
];

export const TANNERY_MENU_GROUPS: MenuStructure[] = [
  {
    id: 'tannery-sales',
    label: 'Sales',
    icon: 'fund',
    openMapKey: 'tannery-sales',
    dataLabel: 'Sales',
    collapsedHeading: 'Sales',
    unitType: TANNERY_UNIT_TYPE,
    children: [
      {
        path: `/${TANNERY_SALE_ORDERS}`,
        label: 'Sale orders',
        icon: 'solution',
        permission: { module: 'Sales - Sale Order', responsibility: 'View' },
        children: [
          { path: `/${TANNERY_SALE_ORDERS}/new`, label: 'New sale order', hidden: true },
          { path: `/${TANNERY_SALE_ORDERS}/:id`, label: 'Sale order', hidden: true },
        ],
      },
      // What is owed on those orders, beside the orders themselves — the person
      // chasing a payment starts from the order it belongs to.
      {
        path: `/${TANNERY_RECEIVABLES}`,
        label: 'Receivables',
        icon: 'account-book',
        permission: { module: 'Sales - Sale Order Payment', responsibility: 'View' },
      },
      // A forecast is raised off confirmed sale orders and read by the store
      // keeper, so it sits with the orders that drive it rather than under
      // production, where nothing creates one.
      {
        path: `/${TANNERY_FORECASTS}`,
        label: 'Chemical forecast',
        icon: 'calculator',
        permission: { module: 'Production - Chemical Forecast', responsibility: 'View' },
        children: [
          { path: `/${TANNERY_FORECASTS}/new`, label: 'New forecast', hidden: true },
          { path: `/${TANNERY_FORECASTS}/:id`, label: 'Forecast', hidden: true },
        ],
      },
    ],
  },
  {
    id: 'tannery-planning',
    label: 'Planning',
    icon: 'schedule',
    openMapKey: 'tannery-planning',
    dataLabel: 'Planning',
    collapsedHeading: 'Planning',
    unitType: TANNERY_UNIT_TYPE,
    children: [
      {
        path: `/${TANNERY_RECIPES}`,
        label: 'Recipes',
        icon: 'experiment',
        permission: { module: 'Production - Recipe', responsibility: 'View' },
        children: [
          { path: `/${TANNERY_RECIPES}/new`, label: 'New recipe', hidden: true },
          { path: `/${TANNERY_RECIPES}/:id`, label: 'Recipe', hidden: true },
        ],
      },
      {
        path: `/${TANNERY_SPEC_SHEETS}`,
        label: 'Spec sheets',
        icon: 'profile',
        permission: { module: 'Production - Spec Sheet', responsibility: 'View' },
        children: [
          { path: `/${TANNERY_SPEC_SHEETS}/new`, label: 'New spec sheet', hidden: true },
          { path: `/${TANNERY_SPEC_SHEETS}/:id`, label: 'Spec sheet', hidden: true },
        ],
      },
      {
        path: `/${TANNERY_PRODUCTION_ORDERS}`,
        label: 'Production orders',
        icon: 'schedule',
        permission: { module: 'Production - Production Order', responsibility: 'View' },
        children: [
          { path: `/${TANNERY_PRODUCTION_ORDERS}/new`, label: 'New production order', hidden: true },
          { path: `/${TANNERY_PRODUCTION_ORDERS}/:id`, label: 'Production order', hidden: true },
        ],
      },
      // Assigns already-planned work to drums ahead of the floor — a
      // planner's screen, so it sits with Production orders rather than with
      // the floor-execution screens below (Batches, Live monitor).
      {
        path: `/${TANNERY_DRUM_SCHEDULE}`,
        label: 'Drum schedule',
        icon: 'calendar',
        permission: { module: 'Production - Drum Schedule', responsibility: 'View' },
      },
    ],
  },
  {
    id: 'tannery-production',
    label: 'Production',
    icon: 'deployment-unit',
    openMapKey: 'tannery-production',
    dataLabel: 'Production',
    collapsedHeading: 'Production',
    unitType: TANNERY_UNIT_TYPE,
    children: [
      // A lot must be registered (and, in practice, graded) before a batch can
      // draw from it, so this sits right before Batches rather than with the
      // masters above — it is receiving data, not configuration.
      {
        path: `/${TANNERY_TRACEABILITY}`,
        label: 'Lot Traceability',
        icon: 'gold',
        permission: { module: 'Production - Traceability', responsibility: 'View' },
        children: [
          { path: `/${TANNERY_TRACEABILITY}/reports`, label: 'Traceability reports', hidden: true },
          { path: `/${TANNERY_TRACEABILITY}/:id`, label: 'Lot', hidden: true },
        ],
      },
      {
        path: `/${TANNERY_BATCHES}`,
        label: 'Batches',
        icon: 'deployment-unit',
        permission: { module: 'Production - Batch', responsibility: 'View' },
        children: [
          { path: `/${TANNERY_BATCHES}/new`, label: 'New batch', hidden: true },
          { path: `/${TANNERY_BATCHES}/:id`, label: 'Batch', hidden: true },
        ],
      },
      // A cross-batch visual overview of one batch at a time, reading the same
      // GET batches/{id} response the batch page itself reads — sits right
      // beside Batches rather than with the floor-execution screens below, the
      // same neighbourhood Live Monitor and Drum Schedule already occupy.
      // Reuses Batches' own `Production - Batch` View (Sprint 15a Phase 6): it is
      // the identical read, not a screen of its own worth a new permission row.
      {
        path: `/${TANNERY_PRODUCTION_FLOW}`,
        label: 'Production Flow',
        icon: 'apartment',
        permission: { module: 'Production - Batch', responsibility: 'View' },
      },
      // What's running right now, derived from the same InProgress stages the
      // drum schedule plans — sits with the floor-execution screens, not with
      // the drum schedule's own planning screen (Planning, above) or the
      // post-completion costing sheet (the shared Costing group, since
      // Sprint 15a's menu redesign).
      {
        path: `/${TANNERY_LIVE_MONITOR}`,
        label: 'Live monitor',
        icon: 'dashboard',
        permission: { module: 'Production - Live Monitor', responsibility: 'View' },
      },
      // Sits with the floor screens, not with the masters: it is opened by the
      // person standing between the drum and the chemical store.
      {
        path: `/${TANNERY_MATERIAL_ISSUE}`,
        label: 'Material issue',
        icon: 'container',
        permission: { module: 'Production - Stage Execution', responsibility: 'Edit' },
      },
      {
        path: `/${TANNERY_GRADING}`,
        label: 'Grading gates',
        icon: 'audit',
        permission: { module: 'Production - Grading', responsibility: 'View' },
        children: [{ path: `/${TANNERY_GRADING}/:stageLogId`, label: 'Grading gate', hidden: true }],
      },
      // Beside Grading, not under Masters: both are quality judgements on a batch,
      // and the QC person who reads one opens the other in the same visit.
      {
        path: `/${TANNERY_LAB_TESTS}`,
        label: 'Lab tests',
        icon: 'experiment',
        permission: { module: 'Production - Lab Test', responsibility: 'View' },
        children: [
          { path: `/${TANNERY_LAB_TESTS}/new`, label: 'New lab test report', hidden: true },
          { path: `/${TANNERY_LAB_TESTS}/:id`, label: 'Lab test report', hidden: true },
        ],
      },
    ],
  },
  // Sprint 19: its own top-level family, per the plan's explicit call — a
  // sample request is raised by sales but tracked through production's pilot
  // and the customer's sign-off, so it does not belong wholly inside Sales
  // (like the plan's own reasoning for giving it a standalone permission
  // module rather than reusing one). One leaf, `Sample` View.
  {
    id: 'tannery-samples',
    label: 'Samples',
    icon: 'experiment',
    permission: { module: 'Sample' },
    openMapKey: 'tannery-samples',
    dataLabel: 'Samples',
    collapsedHeading: 'Samples',
    unitType: TANNERY_UNIT_TYPE,
    children: [
      {
        path: `/${TANNERY_SAMPLES}`,
        label: 'Sample requests',
        icon: 'experiment',
        permission: { module: 'Sample', responsibility: 'View' },
        children: [{ path: `/${TANNERY_SAMPLES}/new`, label: 'New sample request', hidden: true }],
      },
    ],
  },
  // Sprint 20: one consignment leaving the factory (ADR-037). Its own
  // top-level family, like Sales/Planning/Production above.
  // No Gate out leaf here — the plan dropped screen S4 and the
  // `Tannery - Gate Out` permission once the common gate module was
  // confirmed able to see a registered movement (plan §11).
  {
    id: 'tannery-dispatch',
    label: 'Dispatch',
    icon: 'car',
    openMapKey: 'tannery-dispatch',
    dataLabel: 'Dispatch',
    collapsedHeading: 'Dispatch',
    unitType: TANNERY_UNIT_TYPE,
    children: [
      {
        path: `/${TANNERY_OUTWARD_MOVEMENTS}`,
        label: 'Outward movements',
        icon: 'car',
        permission: { module: 'Dispatch - Outward Movement', responsibility: 'View' },
        children: [
          { path: `/${TANNERY_OUTWARD_MOVEMENTS}/new`, label: 'New outward movement', hidden: true },
          { path: `/${TANNERY_OUTWARD_MOVEMENTS}/:id`, label: 'Outward movement', hidden: true },
        ],
      },
      // Sprint 21: goods coming back against a gated-out movement. Sits
      // beside Outward movements in the same Dispatch family — it is the
      // other half of the same document trail, not a separate concern.
      {
        path: `/${TANNERY_INWARD_RETURNS}`,
        label: 'Inward returns',
        icon: 'rollback',
        permission: { module: 'Dispatch - Inward Return', responsibility: 'View' },
        children: [
          { path: `/${TANNERY_INWARD_RETURNS}/new`, label: 'New inward return', hidden: true },
          { path: `/${TANNERY_INWARD_RETURNS}/:id`, label: 'Inward return', hidden: true },
        ],
      },
      // Sprint 22: hides leaving for another tannery and (in Sprint 23) coming
      // back — the same "goods crossing the gate" concern Outward movements
      // and Inward returns already group, not a separate top-level family of
      // its own the way Samples earned one (that one spans sales, production
      // and a customer sign-off; this one is a single dispatch document).
      {
        path: `/${TANNERY_JOB_WORKS}`,
        label: 'Job work',
        icon: 'swap',
        permission: { module: 'Dispatch - Job Work', responsibility: 'View' },
        children: [
          { path: `/${TANNERY_JOB_WORKS}/new`, label: 'New job work order', hidden: true },
          { path: `/${TANNERY_JOB_WORKS}/:id`, label: 'Job work order', hidden: true },
          { path: `/${TANNERY_JOB_WORKS}/:id/receive`, label: 'Receive job work', hidden: true },
        ],
      },
    ],
  },
];
